import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Users, Rocket, Target, ShieldCheck, Handshake, DollarSign, Landmark,
  Briefcase, GraduationCap, Beaker, Vote, Building2, Play, Video, FileText, Sparkles,
  Zap, MessageSquare, BarChart3, Lock, Presentation, ArrowRight,
} from "lucide-react";

const EYE = "text-[10px] uppercase tracking-[0.15em] text-slate-500 font-mono";

// Per-department playbooks — describes exactly HOW each Pandora Management Consult
// department uses the platform, matched against the seeded 25-department org structure.
const DEPARTMENTS = [
  {
    id: "executive_office", name: "Executive Office (MD, EAs)", icon: Landmark, color: "#D4AF37",
    modules: ["Dashboard", "AI Nexus", "Analytics", "Security", "Team"],
    playbook: [
      "Start each morning on the Dashboard — real-time KPIs across every division.",
      "Open AI Nexus → 'Executive Briefing' to auto-generate a 5-minute daily brief from all recent activity.",
      "Review the Security page weekly for MFA compliance, active sessions, and access anomalies.",
      "Approve budgets, hiring and strategy decisions via the Approval Routes on the Organisation page.",
    ],
    example: "MD checks the Vault every Monday morning for signed contracts from the previous week + AI-generated summary of pipeline changes.",
  },
  {
    id: "strategy_advisory", name: "Strategy & Advisory", icon: Target, color: "#7DD3FC",
    modules: ["CRM", "Proposals", "Vault", "AI Nexus", "Knowledge"],
    playbook: [
      "Log every new client conversation as a stakeholder record in CRM.",
      "Turn every discovery call into a proposal draft using Proposals → 'AI draft'.",
      "Store every signed engagement letter, SOW and MoU in the Vault with proper confidentiality tier.",
      "Build reusable frameworks in Knowledge (e.g., '5-Force Analysis Template', 'Growth Diagnostic').",
    ],
    example: "New client onboarding: create stakeholder → upload discovery notes to Knowledge → generate proposal with AI → e-sign in Vault.",
  },
  {
    id: "capital_markets", name: "Capital Markets & Investment", icon: DollarSign, color: "#22C55E",
    modules: ["Capital Pipeline", "Vault", "Analytics", "Finance", "GRC"],
    playbook: [
      "Track every investment opportunity as a deal in Capital Pipeline (kanban stages).",
      "Store all DD documents in the Vault with 'restricted' or 'secret' confidentiality.",
      "Run downside/base/upside scenarios via Finance → Scenarios.",
      "Log every investment risk in GRC → Risk Register (probability × impact).",
    ],
    example: "New PE deal: create pipeline card → invite LPs to Vault DD room → track access logs → trigger investment committee approval.",
  },
  {
    id: "grc_compliance", name: "Governance, Risk & Compliance", icon: ShieldCheck, color: "#F0A6A6",
    modules: ["GRC", "Vault", "Security", "Audit"],
    playbook: [
      "Maintain the enterprise Risk Register in GRC → Risks with owners + mitigation.",
      "Run quarterly Control Testing cycles in GRC → Controls.",
      "Investigate every incident + ethics report through GRC → Incidents.",
      "Store audit-ready evidence packs in the Vault (immutable, versioned).",
    ],
    example: "Bank of Ghana AML audit prep: pull the last 90 days of audit logs → export as PDF → attach to a GRC Control test → distribute to auditor.",
  },
  {
    id: "operations", name: "Operations & Delivery", icon: Rocket, color: "#8B5CF6",
    modules: ["Projects", "Tasks", "Automation", "Meetings"],
    playbook: [
      "Every client engagement is a Project with milestones, tasks and owner.",
      "Kanban board on the Projects page for status transparency.",
      "Set up Automation rules to auto-remind, auto-escalate, auto-report.",
      "Run all client & internal syncs via Meetings — recorded, transcribed, action-item-tagged.",
    ],
    example: "Delivery lead auto-generates a weekly client status report every Friday 4pm via an Automation rule.",
  },
  {
    id: "hr_people", name: "Human Resources & People", icon: Users, color: "#EC4899",
    modules: ["Team", "Organisation", "Vault", "Knowledge"],
    playbook: [
      "Onboard new staff via Team → Import CSV — the platform emails their credentials automatically.",
      "Assign each new hire to a position on the Organisation → Directory tab.",
      "Store all HR docs (offer letters, appraisals, warnings) in the Vault under 'HR' confidentiality.",
      "Publish company handbooks + SOPs in Knowledge for whole-team access.",
    ],
    example: "New hire day 1: HR admin invites them via CSV → assigns to 'Senior Analyst · Advisory' position → new hire gets welcome email with credentials.",
  },
  {
    id: "finance_treasury", name: "Finance & Treasury", icon: BarChart3, color: "#F59E0B",
    modules: ["Finance", "Billing", "Analytics", "Vault"],
    playbook: [
      "Model P&L, cash flow and treasury scenarios in Finance → Scenarios.",
      "Track subscription and consulting revenue in Billing.",
      "Build custom finance dashboards in Analytics.",
      "Store audited financial statements in the Vault (immutable, timestamped).",
    ],
    example: "CFO builds a rolling 13-week cash forecast in Finance → publishes it to the Executive team via Analytics dashboard.",
  },
  {
    id: "marketing_business_dev", name: "Marketing & Business Development", icon: Handshake, color: "#3B82F6",
    modules: ["CRM", "AI Nexus", "Analytics", "Communications"],
    playbook: [
      "Track every prospect from first touch → close in CRM.",
      "Use AI Nexus → 'Cold outreach' to draft personalised prospecting emails.",
      "Measure BD funnel conversion in Analytics.",
      "Log every prospect meeting note in the stakeholder timeline.",
    ],
    example: "BD analyst pulls a list of Tier-1 stakeholders in Ghana banking sector → AI drafts 15 personalised intro emails → each opened is logged.",
  },
  {
    id: "research", name: "Research & Knowledge Management", icon: Beaker, color: "#06B6D4",
    modules: ["Knowledge", "AI Nexus", "Analytics", "Vault"],
    playbook: [
      "Curate frameworks, methodologies, and past research in Knowledge.",
      "Ask AI Nexus questions against your Knowledge base — cited answers only.",
      "Publish reports and insights via Analytics dashboards.",
      "Version-control every research doc in the Vault.",
    ],
    example: "Researcher writes a Ghana fintech landscape report → publishes to Knowledge → the whole firm can now ask 'What's Ghana's fintech regulatory outlook?' and get a cited answer.",
  },
  {
    id: "government_affairs", name: "Government Affairs & Policy", icon: Vote, color: "#EF4444",
    modules: ["Government Suite", "CRM", "Vault", "AI Nexus"],
    playbook: [
      "Track policy dossiers, bill drafts and government stakeholders.",
      "Store cabinet memos, position papers, and ministerial briefings in the Vault (restricted tier).",
      "Use AI Nexus to draft cabinet memos in your firm's style.",
      "Log every regulator, minister and MP interaction as a stakeholder in CRM.",
    ],
    example: "Government relations lead uses AI Nexus to draft a Ministry of Finance position paper → routes it through the Approval flow → e-signs → delivers.",
  },
  {
    id: "grants_ma_e", name: "Grants & M&E (Development)", icon: GraduationCap, color: "#10B981",
    modules: ["Grants (P0)", "Projects", "Analytics", "Vault"],
    playbook: [
      "Track every donor programme, logframe indicator, and disbursement schedule.",
      "Measure programme outcomes against SDG-linked indicators.",
      "Store every donor MoU and reporting template in the Vault.",
      "Auto-generate mid-year and year-end donor reports.",
    ],
    example: "M&E officer inputs quarterly outcome data → dashboard shows exact % attainment vs logframe → auto-generates donor progress report.",
  },
  {
    id: "corporate_support", name: "Corporate Support (IT, Legal, Admin)", icon: Briefcase, color: "#94A3B8",
    modules: ["Security", "Vault", "Team", "Automation"],
    playbook: [
      "Grant + revoke access via Team → member management.",
      "Store every legal contract, NDA and vendor agreement in the Vault.",
      "Manage system-wide security posture (MFA, IP allowlist, sessions) in Security.",
      "Create Automation rules for internal ops (expense approvals, timesheet reminders).",
    ],
    example: "IT admin sets an Automation rule: every 90 days, remind staff who haven't rotated their password.",
  },
];

export default function Guide() {
  const [expanded, setExpanded] = useState("executive_office");

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="guide-page">
      <div>
        <div className={EYE + " mb-1"}>// PLATFORM USAGE GUIDE</div>
        <h1 className="font-display text-3xl text-white tracking-tight">How Pandora Management Consult uses Pandora Nexus</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          A per-department playbook showing exactly which modules each team relies on, the daily rituals that make it work, and one concrete example. Click any department to expand.
        </p>
      </div>

      {/* Quickstart tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickTile icon={Play} label="1. Set up your profile" desc="Update name, email, and enable 2FA" href="/profile" />
        <QuickTile icon={Users} label="2. Invite your team" desc="Import CSV or invite individually" href="/team" />
        <QuickTile icon={Building2} label="3. Assign to positions" desc="Fill vacant slots on the Org Chart" href="/organisation" />
      </div>

      {/* Department accordions */}
      <div className="space-y-3">
        <div className={EYE}>// DEPARTMENT PLAYBOOKS</div>
        {DEPARTMENTS.map((d, i) => {
          const isOpen = expanded === d.id;
          const Icon = d.icon;
          return (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="nexus-card overflow-hidden"
              data-testid={`dept-guide-${d.id}`}
            >
              <button
                onClick={() => setExpanded(isOpen ? null : d.id)}
                className="w-full p-4 flex items-center gap-4 text-left hover:bg-white/[0.02]"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${d.color}20`, border: `1px solid ${d.color}40` }}>
                  <Icon className="w-4 h-4" style={{ color: d.color }} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium">{d.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                    Modules · {d.modules.join(" · ")}
                  </div>
                </div>
                <ArrowRight className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>

              {isOpen && (
                <div className="border-t border-white/5 p-5 space-y-4">
                  <div>
                    <div className={EYE + " mb-2"}>// DAILY PLAYBOOK</div>
                    <ol className="space-y-2">
                      {d.playbook.map((step, si) => (
                        <li key={si} className="flex gap-3">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 mt-0.5"
                            style={{ background: `${d.color}22`, color: d.color }}>{si + 1}</div>
                          <div className="text-sm text-slate-300 leading-relaxed">{step}</div>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div className="p-3 border border-white/5 bg-[#0A111F]/60">
                    <div className={EYE + " mb-1.5"}>// CONCRETE EXAMPLE</div>
                    <div className="text-sm text-slate-300 leading-relaxed italic">{d.example}</div>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Feature cheatsheet */}
      <div className="nexus-card p-6">
        <div className={EYE + " mb-3"}>// FEATURE CHEATSHEET</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          <Cheat icon={FileText} label="Vault" desc="Contracts, MOUs, board papers — versioned + encrypted" />
          <Cheat icon={Sparkles} label="AI Nexus" desc="Briefings, memos, drafts using Claude Sonnet 4.5" />
          <Cheat icon={Users} label="CRM" desc="Stakeholder relationships & communication timeline" />
          <Cheat icon={Rocket} label="Projects" desc="Kanban with milestones + auto-status reports" />
          <Cheat icon={Zap} label="Automation" desc="Rules for reminders, escalations, workflows" />
          <Cheat icon={BarChart3} label="Analytics" desc="Custom dashboards & data exploration" />
          <Cheat icon={ShieldCheck} label="GRC" desc="Risk register, controls, incident tracking" />
          <Cheat icon={MessageSquare} label="Communications" desc="Logged emails, calls, meetings against stakeholders" />
          <Cheat icon={Lock} label="Security" desc="MFA, IP allowlist, sessions, email diagnostics" />
          <Cheat icon={Presentation} label="Meetings" desc="Live video + slide presentations (coming soon)" />
          <Cheat icon={Building2} label="Organisation" desc="Departments, positions, approval routes" />
          <Cheat icon={BookOpen} label="Knowledge" desc="Reusable frameworks, SOPs, research library" />
        </div>
      </div>
    </div>
  );
}

const QuickTile = ({ icon: Icon, label, desc, href }) => (
  <a href={href} className="nexus-card p-4 flex items-start gap-3 hover:border-[#D4AF37]/30 group transition">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
      style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
      <Icon className="w-4 h-4 text-[#D4AF37]" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm text-white font-medium group-hover:text-[#FCE588]">{label}</div>
      <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
    </div>
  </a>
);

const Cheat = ({ icon: Icon, label, desc }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37] shrink-0" />
    <div className="min-w-0">
      <div className="text-white font-medium text-[13px]">{label}</div>
      <div className="text-[11px] text-slate-500 leading-snug">{desc}</div>
    </div>
  </div>
);
